/**
 * © [2024] MeaningfulBeauty. All rights reserved.
 * 
 * Implementation Overview:
 * This JavaScript module manages personalization logic on a Shopify store.
 * It dynamically updates page sections, injects custom CSS, and executes custom JavaScript based on query parameters found in the URL.
 * The personalization data is sourced from a JSON object passed from Liquid.
 * The script identifies relevant personalization based on query parameters, applies matching personalization,
 * and loads new section content based on identified personalization object while injecting custom CSS and executing custom JavaScript as needed.
 * 
 * Key features:
 * - Detects any custom query parameters in the URL to determine active personalization.
 * - Fetches and updates page sections using the Shopify section API.
 * - Injects custom CSS and executes custom JavaScript into the page when required.
 * - Includes a debug mode for detailed logging.
 */
// PersonalizationController: Manages interactions, configuration, and debugging logic
const PersonalizationController = {
    // Global settings
    execute_script: isExecuteScript, // Determines if the script should run (based on Liquid logic)
    debugMode: false, // Set to 'false' to turn off logging for production
    activePersonalization: null, // New property to hold active personalization  
    sectionAssignedApplicablePersonalizationData: sectionMetaApplicablePersonalizationData,
    itc_url: null,
    utm_url: null,


    // Logging function for debug purposes
    log: function (message, data = null) {
        if (this.debugMode) {
            console.log(message, data);
        }
    },


    // Logging function for debug purposes
    log: function (message, data = null) {
        if (this.debugMode) {
            console.log(message, data);
        }
    },

    // Initializes the Personalization Controller, fetching personalization data and processing campaigns
    init: function () {
        // Assign values directly, checking both URL and session
        this.itc_url = this.getUrlParameter('itc') || this.getCustomerSegmentValue('itc');
        this.utm_url = this.getUrlParameter('utm_medium') || this.getCustomerSegmentValue('utm_medium');

        this.saveCustomerSegment(); // saving url paramter in global session
        this.log("Initializing Personalization Controlle with sectionMetaApplicablePersonalizationData is ", sectionMetaApplicablePersonalizationData);
        this.log("Emoty >>>>", this.isEmptyObject(sectionMetaApplicablePersonalizationData));

        // Check if sectionMetaApplicablePersonalizationData has any data
        if (!sectionMetaApplicablePersonalizationData || Object.keys(sectionMetaApplicablePersonalizationData).length === 0 ||
            this.isEmptyObject(sectionMetaApplicablePersonalizationData)) {
            this.log("Error: No personalization data found in sectionMetaApplicablePersonalizationData.");
            View.removeFLickerAdjustmentClass();//Show the visibility - Refer theme.liquid mb-personalization-loading class
            return; // Stop execution if there is no data
        }
        // else part fecth the customersgment based on url paramter  

        Object.keys(sectionMetaApplicablePersonalizationData).forEach(key => {
            // Parse the value associated with the key
            const parsedData = JSON.parse(sectionMetaApplicablePersonalizationData[key]);

            // Add the `qualified` property to each item
            parsedData.forEach(item => {
                item.qualified = false; // Inject `qualified` with an initial value
            });

            // Update the global object with the modified data
            sectionMetaApplicablePersonalizationData[key] = JSON.stringify(parsedData);
        });



        // Filter the personalization data based on date range and active status
        const filteredData = this.filterPersonalizationData(sectionMetaApplicablePersonalizationData);

        // Check if filteredData is empty
        if (Object.keys(filteredData).length === 0) {
            this.log("No valid personalization data available after filtering.");
            View.removeFLickerAdjustmentClass();//Show the visibility - Refer theme.liquid mb-personalization-loading class
            return; // Stop execution if there is no data
            // You may want to stop further processing or load a default experience
        } else {

            Model.identifyQualifiedURLWithSessionManagement(filteredData);
            this.loadPersonalizationContent(filteredData);

        }
    },
    // Reusable function to filter the personalization data based on date range and active status
    filterPersonalizationData: function (sectionMetaApplicablePersonalizationData) {
        // New object to store filtered data
        const filteredData = {};

        // Iterate over the keys in sectionMetaApplicablePersonalizationData
        Object.keys(sectionMetaApplicablePersonalizationData).forEach(key => {
            const value = sectionMetaApplicablePersonalizationData[key];
            try {
                // Parse the value (which is in JSON string format)
                const parsedData = JSON.parse(value);
                this.log("Parsed Section Data:", parsedData);

                // Filter the data based on date range and active status
                const validItems = parsedData.filter(item => {
                    const { start_date, end_date, active } = item;

                    // Log the dates and active status
                    this.log(`Checking item ${item.unique_identifier}: start_date=${start_date}, end_date=${end_date}, active=${active}`);

                    // Skip invalid items (missing date or active flag, or invalid date)
                    if (!start_date || !end_date || active === undefined) {
                        this.log(`Skipping invalid item: ${item.unique_identifier}`);
                        return false; // Skip invalid item
                    }

                    // Check if the item is within the valid date range
                    const isValidDate = this.isDateInRange(new Date(start_date), new Date(end_date));
                    return isValidDate && active; // Only keep items that are active and within the date range
                });

                // If there are valid items, store them back in the filteredData object
                if (validItems.length > 0) {
                    filteredData[key] = JSON.stringify(validItems); // Convert back to string format
                } else {
                    this.log(`No valid items for section: ${key}`);
                }

            } catch (error) {
                this.log("Error parsing JSON data for key:", key, error);
            }
        });

        this.log("Filtered Data:", filteredData);
        return filteredData;
    },
    // Retrieves the value of a specific query parameter from the URL
    getUrlParameter: function (name) {
        this.log("Fetching URL parameter:", name);
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(name);
    },

    //save customer segement in session

    saveCustomerSegment: function () {

        const itc_url = this.getUrlParameter('itc');
        const utm_url = this.getUrlParameter('utm_medium');

        if (itc_url || utm_url) {
            const customerSegment = {
                itc: itc_url,
                utm_medium: utm_url
            };

            // Save in session storage
            sessionStorage.setItem('customerSegment', JSON.stringify(customerSegment));
            console.log("Customer segment saved:", customerSegment);
        }
    },

    // Retrieve data from URL first, if not available then from session storage
    getCustomerSegmentValue: function (key) {
        const savedSegment = sessionStorage.getItem('customerSegment');
        if (savedSegment) {
            const parsedSegment = JSON.parse(savedSegment);
            return parsedSegment[key] || null;
        }
        return null;
    },

    // Injects custom CSS into the page if provided in the campaign data
    injectCustomCSS: function (cssContent) {
        if (cssContent) {
            this.log("Injecting custom CSS...");
            const style = document.createElement('style');
            style.type = 'text/css';
            style.appendChild(document.createTextNode(cssContent));
            document.head.appendChild(style);
            this.log("Custom CSS injected successfully.");
        }
    },

    // Injects custom JavaScript into the page if provided in the campaign data
    injectCustomJS: function (jsContent) {
        if (jsContent) {
            this.log("Injecting custom JavaScript...");
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.appendChild(document.createTextNode(jsContent));
            document.body.appendChild(script);
            this.log("Custom JavaScript injected successfully.");
        }
    },

    // Reusable function to check if the current date is within start and end date range (in EST)
    isDateInRange: function (startDate, endDate) {

        this.log("Start Date :", startDate);
        this.log("End Date :", endDate);

        let now = new Date();
        let estNow = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));

        // Convert start and end dates to EST
        let estStartDate = new Date(startDate.toLocaleString('en-US', { timeZone: 'America/New_York' }));
        let estEndDate = new Date(endDate.toLocaleString('en-US', { timeZone: 'America/New_York' }));

        this.log("Checking if current date is within the range:");
        this.log("Current Time (EST):", estNow);
        this.log("Start Date (EST):", estStartDate);
        this.log("End Date (EST):", estEndDate);

        return estNow >= estStartDate && estNow <= estEndDate;
    },
    // Check if the object contains only empty arrays as values
    isEmptyObject: function (obj) {
        for (const key in obj) {
            const parsedValue = JSON.parse(obj[key]);
            // Check if the parsed value is an empty array
            if (Array.isArray(parsedValue) && parsedValue.length > 0) {
                return false; // If any array has data, return false
            }
        }
        return true; // All arrays are empty
    },

    // Loads and updates the content of specified page sections based on campaign data
    loadPersonalizationContent: function (sectionAssignedApplicablePersonalizationData) {
        this.log("Loading personalization content...");
        this.log("Received Section Level Personalization Data:", sectionAssignedApplicablePersonalizationData);

        // Create a list of promises for the fetch operations
        const fetchPromises = [];

        // Iterate over the keys in sectionAssignedApplicablePersonalizationData
        Object.keys(sectionAssignedApplicablePersonalizationData).forEach(key => {

            this.log("Each Section Data key:", key);

            const value = sectionAssignedApplicablePersonalizationData[key];

            // Parse the value as it's in JSON string format
            const parsedData = JSON.parse(value);
            this.log("Each Section Data:", parsedData);

            // Check each item for matching unique identifiers
            parsedData.forEach(item => {

                if (item.qualified) {
                    this.log("Qualified for the campaign:", item.unique_identifier);
                    const sectionToReplace = item.section; // Assuming you want the first page
                    this.log("Section to Replace:", sectionToReplace);
                    if (sectionToReplace) {
                        // Select the section to replace in the DOM
                        //const sectionElement = document.querySelector(`section[class*="${key}"]`);
                        //const sectionElement = document.querySelector(`div[data-attr="${key}"]`);

                         let sectionElement = document.querySelector(`section[class*="${key}"]`) 
                         || document.querySelector(`div[data-attr="${key}"]`);

                        this.log("Key:", key);
                        this.log("Section Element:", sectionElement);
                        if (sectionElement) {
                            const fetchPromise = fetch(`/?sections=${sectionToReplace}`)
                                .then(response => response.json())
                                .then(data => {
                                    this.log("Section data fetched successfully for:", key);
                                    View.updateSectionContent(key, sectionToReplace, data);
                                    PersonalizationController.injectCustomCSS(item.custom_css);
                                    PersonalizationController.injectCustomJS(item.custom_js);
                                })
                                .catch(error => {
                                    this.log('Error fetching section:', error);
                                });
                            // Push the promise to the list of fetch promises
                            fetchPromises.push(fetchPromise);
                            this.log("fetchPromise:", fetchPromise);
                        } else {
                            this.log(`Section element for ${key} not found.`);
                        }
                    } else {
                        if (item.custom_css) {
                            PersonalizationController.injectCustomCSS(item.custom_css);
                        }
                        if (item.custom_js) {
                            PersonalizationController.injectCustomJS(item.custom_js);
                        }
                    }
                }
            });
        });
        // Once all fetch operations are complete, remove the flicker adjustment class
        Promise.all(fetchPromises)
            .then(() => {
                this.log("All sections updated, removing flicker adjustment class.");
            })
            .catch(error => {
                this.log("Error completing personalization updates:", error);
            })
            .finally(() => {
                // Ensure the flicker class is removed no matter the result
                View.removeFLickerAdjustmentClass();
            });
    }
};

// Model: Handles data logic and interacts with personalization data
const Model = {
    personalizationGlobalMetaData: null,
    // Finds the matching campaign based on query parameters or session data, only if the campaign is active
    identifyQualifiedURLWithSessionManagement: function (sectionMetaApplicablePersonalizationData) {
        PersonalizationController.log("Finding matching campaign using section-level data...", sectionMetaApplicablePersonalizationData);
        let detectedPersonalization = false;
        let qualifyCustomerSegmentFlag =false;
        PersonalizationController.log("sectionMetaApplicablePersonalizationData>>>>", sectionMetaApplicablePersonalizationData);
        // Iterate over the keys in sectionMetaApplicablePersonalizationData

        Object.keys(sectionMetaApplicablePersonalizationData).forEach(key => {
            const value = sectionMetaApplicablePersonalizationData[key];
            try {
                // Parse the value as it's in JSON string format
                const parsedData = JSON.parse(value);
                PersonalizationController.log("Parsed section-level data for key:", key, parsedData);

                //Iterating each personalization on the given section
                parsedData.forEach(item => {
                    item.qualified = false;
                    detectedPersonalization = false;

                    // Check if the item is active
                    let customerSegment = JSON.parse(item.customersegment);
                     qualifyCustomerSegmentFlag = Model.checkCustomerSegmentQualify(customerSegment);
                    if(qualifyCustomerSegmentFlag){
                        item.qualified = true;
                    }
                    PersonalizationController.log("qualifyCustomerSegmentFlag", qualifyCustomerSegmentFlag);

                    if (qualifyCustomerSegmentFlag) { 
                        Model.savePersonalizationAndAnalyticsInSession(customerSegment,item);
                    }
                    return item;


                });
                sectionMetaApplicablePersonalizationData[key] = JSON.stringify(parsedData);

            } catch (error) {
                console.error('Error parsing section-level data:', error);
            }
        });


        if (!qualifyCustomerSegmentFlag) {
            PersonalizationController.log("No detected personalization, checking session storage.");

            // Retrieve stored personalization data from sessionStorage
            let storedPersonalizations = JSON.parse(sessionStorage.getItem("personalization")) || [];

            if (!Array.isArray(storedPersonalizations)) {
                storedPersonalizations = [storedPersonalizations]; // Ensure data is an array
            }

            PersonalizationController.log("Session Personalizations:", storedPersonalizations);

            if (storedPersonalizations.length > 0) {
                Object.entries(sectionMetaApplicablePersonalizationData).forEach(([key, value]) => {
                    let parsedData = JSON.parse(value);
                    let updatedData = parsedData.map(item => {
                        let customerSegment = JSON.parse(item.customersegment);

                        // Check if this personalization exists in session data
                        let matchesSession = storedPersonalizations.some(sessionData => {
                            return sessionData.personalizations.some(sessionItem => {

                                if (sessionItem.ab_tasty_qualifier) {
                                    return (
                                        customerSegment.itc === sessionItem.itc &&
                                        customerSegment.utm_medium === sessionItem.utm_medium &&
                                        customerSegment.experiment_id === sessionItem.experiment_id &&
                                        customerSegment.variation_id === sessionItem.variation_id
                                    );
                                } else {
                                    PersonalizationController.log("in else");

                                    return (
                                        customerSegment.itc === sessionItem.itc &&
                                        customerSegment.utm_medium === sessionItem.utm_medium
                                    );
                                }
                            });
                        });

                        PersonalizationController.log("matchesSession result:", matchesSession);

                        return matchesSession ? { ...item, qualified: true } : item;
                    });

                    // Update personalization data if at least one match was found
                    if (updatedData.some(item => item.qualified)) {
                        detectedPersonalization = true;
                        sectionMetaApplicablePersonalizationData[key] = JSON.stringify(updatedData);
                    }

                    PersonalizationController.log("updatedData:", updatedData);
                });

                if (detectedPersonalization) {
                    PersonalizationController.log("Matching campaigns found in session storage.", detectedPersonalization);
                }
            }

        }


        PersonalizationController.log("DetectedPersonalization:", detectedPersonalization);
    },


// Based on the customer segment from the metaobject, we check if the utm_medium and itc values match the URL and customer segment.  
// Additionally, if 'abtatsy qualify' is enabled, we verify whether the experimentID and variationID match the customer segment.  
// Based on this, we set detectedPersonalization to true.

    checkCustomerSegmentQualify: function (customerSegment) {

        // Check if the item is active 
        let detectedPersonalization = false;
        let itc_customer_segment = customerSegment.itc;
        let utm_medium_customer_segment = customerSegment.utm_medium;
        let experiment_id = customerSegment.experiment_id;
        let ab_tasty_qualifier = customerSegment.abtasty_qualifier;
        let variation_customer_segment = customerSegment.variation_id;
        let identifier = customerSegment.identifier;

        // Matching conditions
        let isItcMatch = PersonalizationController.itc_url && itc_customer_segment && PersonalizationController.itc_url.includes(itc_customer_segment);
        let isUtmMatch = utm_medium_customer_segment && PersonalizationController.utm_url === utm_medium_customer_segment;
        let isOnlyUtmMatch = isUtmMatch && !itc_customer_segment;  // utm matches but itc is empty
        let isOnlyItcMatch = isItcMatch && !utm_medium_customer_segment; // itc matches but utm is empty

        let hasABTastyResults = Object.keys(window.ABTasty?.results || {}).length > 0;
        PersonalizationController.log("hasABTastyResults:", hasABTastyResults);
        if (hasABTastyResults && ab_tasty_qualifier) {
            for (const [experimentKey, experimentData] of Object.entries(window.ABTasty.results)) {
                if (((isItcMatch && isUtmMatch) || isOnlyUtmMatch || isOnlyItcMatch) && experimentKey == customerSegment.experiment_id && experimentData.variationID == customerSegment.variation_id) {
                    detectedPersonalization = true;
                }
            }
        } else if ((isItcMatch && isUtmMatch) || isOnlyUtmMatch || isOnlyItcMatch) {
            detectedPersonalization = true;
        }
        return detectedPersonalization;
    },

// This function saves personalization data in sessionStorage and variableMap.  
// It checks if the personalization already exists in the session to avoid duplicate entries based on UTM and ITC.  
// If the personalization exists, it is not added. If it is unique, it gets stored.


    savePersonalizationAndAnalyticsInSession: function (customerSegmentObject,personalizationObject) {
        // Retrieve existing personalization data from sessionStorage
        let existingPersonalizationData = JSON.parse(sessionStorage.getItem("personalization")) || {
            personalizations: []
        };
        // Ensure `personalizations` is an array
        if (!Array.isArray(existingPersonalizationData.personalizations)) {
            existingPersonalizationData.personalizations = [];
        }

        // Create a new personalization entry
        let newPersonalization = {
            personalization_identifier: personalizationObject.unique_identifier,
            customerSegment_identifier: customerSegmentObject.identifier,
            itc: customerSegmentObject.itc,
            utm_medium: customerSegmentObject.utm_medium,
            ab_tasty_qualifier: customerSegmentObject.ab_tasty_qualifier,
            experiment_id: customerSegmentObject.experiment_id,
            variation_id: customerSegmentObject.variation_id
        };
        // Add new personalization **only if it's not already present**
        const exists = existingPersonalizationData.personalizations.some(p =>
            p.itc === newPersonalization.itc && p.utm_medium === newPersonalization.utm_medium
        );

        if (!exists) {
            existingPersonalizationData.personalizations.push(newPersonalization);
        }

        // Save updated personalization data
        sessionStorage.setItem("personalization", JSON.stringify(existingPersonalizationData));
        // Store only the required fields in app.variableMap.personalizations
        app.variableMap.personalizations = {
            personalization_identifier: existingPersonalizationData.personalizations.map(p => p.personalization_identifier),
            customerSegment_identifier: customerSegmentObject.identifier
        };
    }
};

// View: Responsible for updating the UI with new section content
const View = {
    // Updates the content of a section identified by a reference class ID
    updateSectionContent: function (referenceClassId, sectionName, data) {
        PersonalizationController.log("Updating section content for:", referenceClassId);
        PersonalizationController.log("Updating section content with data:", data);

        // Construct the ID based on referenceClassId
        const sectionId = `shopify-section-${referenceClassId}`;

        // Select the section element using the constructed ID
        const sectionElement = document.querySelector(`#${sectionId}`);
        PersonalizationController.log("sectionElement", sectionElement);

        if (sectionElement) {
            // Replace only the inner HTML content of the section
            sectionElement.innerHTML = data[`${sectionName}`];
            PersonalizationController.log("Section content updated successfully for:", referenceClassId);
        } else {
            PersonalizationController.log("Section element not found for:", referenceClassId);
        }

    },
    removeFLickerAdjustmentClass: function () {
        document.documentElement.classList.remove("mb-personalization-loading");
    }
};

// Run when the DOM is fully loaded
document.addEventListener("DOMContentLoaded", () => {
    console.log('DOMContentLoaded event triggered');
    PersonalizationController.init();

    // Check if app.variableMap.personalizations is empty
    if ((!app.variableMap.personalizations ||
        (typeof app.variableMap.personalizations === 'object' && Object.keys(app.variableMap.personalizations).length === 0) ||
        (Array.isArray(app.variableMap.personalizations) && app.variableMap.personalizations.length === 0))) {

        // Retrieve stored personalization data from sessionStorage
        const storedPersonalizationData = JSON.parse(sessionStorage.getItem("personalization")) || { personalizations: [] };

        if (storedPersonalizationData.personalizations && storedPersonalizationData.personalizations.length > 0) {
            PersonalizationController.log("Restoring personalization data from session storage:", storedPersonalizationData);


            // Extract all personalization identifiers and customer segment identifiers
            app.variableMap.personalizations = {
                personalization_identifier: storedPersonalizationData.personalizations.map(p => p.personalization_identifier),
                customerSegment_identifier: storedPersonalizationData.personalizations.length > 0
                    ? storedPersonalizationData.personalizations[storedPersonalizationData.personalizations.length - 1].customerSegment_identifier
                    : null,
                qualified: storedPersonalizationData.personalizations.some(p => p.qualified)
            };


            PersonalizationController.log("app.variableMap updated with personalizations:", app.variableMap.personalizations);
        }
    }

    // Remove the flicker adjustment class
    View.removeFLickerAdjustmentClass();
});


